import { useCallback } from 'react';
import type { JSX } from 'react';
import { useToast } from '@pyranger/ui';
import type {
    CustomAlertUtils,
    ShowCustomAlert,
    ShowCustomAlertInput,
    ToastVariant,
} from '@pyranger/types';
import {
    ExclamationTriangleIcon,
    InformationCircleIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

const iconMap: Record<ToastVariant, JSX.Element> = {
    error: <ExclamationTriangleIcon width={24} height={24} />,
    info: <InformationCircleIcon width={24} height={24} />,
    success: <CheckCircleIcon width={24} height={24} />,
    warning: <ExclamationCircleIcon width={24} height={24} />,
};

/**
 * Implements a custom alert dialog by calling the `useToast` hook.
 * @returns the {@link CustomAlertUtils}
 */
export const useCustomAlert = (): CustomAlertUtils => {
    const { notify } = useToast();

    const showCustomAlert = useCallback<ShowCustomAlert>(
        ({ duration, title, message, link, variant }: ShowCustomAlertInput) => {
            notify({
                duration,
                message: (
                    <div>
                        <p className="text-xl leading-5">{title}</p>
                        <p className="whitespace-pre-wrap pt-1">{message}</p>
                        {link && (
                            <p>
                                {navigator.onLine && (
                                    <a
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {link.text}
                                    </a>
                                )}
                                {!navigator.onLine && (
                                    <p>{`${link.text}: ${link.url}`}</p>
                                )}
                            </p>
                        )}
                    </div>
                ),
                variant,
                icon: iconMap[variant],
            });
        },
        [notify],
    );

    return {
        showCustomAlert,
    };
};
